import { dateMedium, time } from '@aioe/shared/wording/dateTime';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { RENEWED_EVENT } from './auth/context.ts';

/**
 * Load once, reload on demand or on an interval. `deps` identify the query; the loader is read through a ref.
 *
 * `loading` is true until the current query has answered once, and while a `reload()` asked for by
 * the person (or a renewed sign-in) is out. A refresh on the interval loads in the background and
 * does not set it, so a view does not flash its empty or waiting state every interval. An answer
 * to a query that has since changed is dropped.
 *
 * `whenVisible`: the interval does not ask while the tab is hidden, and asks once when it shows again
 * if a tick was skipped meanwhile; for views that poll a machine through the platform.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = [], intervalMs?: number, opts: { whenVisible?: boolean } = {}) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  /** The query the last answer was for: until it is this one, the view is loading. */
  const [settledKey, setSettledKey] = useState<string>();
  const [reloading, setReloading] = useState(false);
  const loader = useRef(load);
  const key = JSON.stringify(deps);
  const latest = useRef(key);
  // Kept current after each render; the effects below that load run after this one.
  useEffect(() => {
    loader.current = load;
    latest.current = key;
  });
  // `key` is the query: a new one makes a new `fetchNow`, which the effects below load again with.
  const fetchNow = useCallback(() => {
    loader
      .current()
      .then((d) => {
        if (latest.current !== key) return;
        setData(d);
        setError(undefined);
      })
      .catch((e: Error) => {
        if (latest.current === key) setError(e.message);
      })
      .finally(() => {
        if (latest.current !== key) return;
        setSettledKey(key);
        setReloading(false);
      });
  }, [key]);
  const reload = useCallback(() => {
    setReloading(true);
    fetchNow();
  }, [fetchNow]);
  // A view that failed while the sign-in waited to be renewed loads again once it is.
  const failed = useRef(false);
  useEffect(() => {
    failed.current = Boolean(error);
  }, [error]);
  useEffect(() => {
    const again = () => {
      if (failed.current) reload();
    };
    window.addEventListener(RENEWED_EVENT, again);
    return () => window.removeEventListener(RENEWED_EVENT, again);
  }, [reload]);
  const whenVisible = opts.whenVisible === true;
  useEffect(() => {
    fetchNow();
    if (!intervalMs) return;
    let missed = false;
    const hidden = () => whenVisible && typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const t = setInterval(() => {
      if (hidden()) missed = true;
      else fetchNow();
    }, intervalMs);
    const shown = () => {
      if (missed && !hidden()) {
        missed = false;
        fetchNow();
      }
    };
    if (whenVisible) document.addEventListener('visibilitychange', shown);
    return () => {
      clearInterval(t);
      if (whenVisible) document.removeEventListener('visibilitychange', shown);
    };
  }, [fetchNow, intervalMs, whenVisible]);
  return { data, error, loading: reloading || settledKey !== key, reload };
}

/** How often the shared clock moves on while something shows it. */
const CLOCK_TICK_MS = 30_000;
let clockNow = Date.now();
const clockListeners = new Set<() => void>();
let clockTimer: ReturnType<typeof setInterval> | undefined;

function subscribeClock(listener: () => void): () => void {
  // The first view to show the time reads it fresh; React compares the snapshot after subscribing.
  if (!clockListeners.size) clockNow = Date.now();
  clockListeners.add(listener);
  clockTimer ??= setInterval(() => {
    clockNow = Date.now();
    for (const l of clockListeners) l();
  }, CLOCK_TICK_MS);
  return () => {
    clockListeners.delete(listener);
    if (!clockListeners.size && clockTimer) {
      clearInterval(clockTimer);
      clockTimer = undefined;
    }
  };
}
const clockSnapshot = () => clockNow;

/**
 * The time now, in milliseconds, for a render: one clock shared by every view, moving on every 30
 * seconds while anything shows it. A render stays pure (the same answer however often React calls
 * it), and what depends on the time (how long a run has gone, whether a secret is due) moves on by
 * itself.
 */
export function useNow(): number {
  return useSyncExternalStore(subscribeClock, clockSnapshot);
}

export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return `${Math.round(s)} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/**
 * A moment in the person's words (their clock and date order, wording/dateTime.ts): "Fri 24 Oct, 3:12 pm", with the
 * year when it is not this one ("Fri 24 Oct 2025, 3:12 pm"). Takes an ISO time or epoch ms.
 */
export function dateTimeWords(at: string | number, now = new Date()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '—';
  return `${dateMedium(d, { weekday: true, year: 'auto', now: now.getTime() })}, ${time(d)}`;
}

/**
 * State that survives the page going to the identity provider and back: kept in sessionStorage
 * under `key` while it differs from `initial`, cleared by `discard` (or once saved). `key`
 * undefined keeps it in memory only. A renewal never loses typed text.
 */
export function useDraft<T>(key: string | undefined, initial: T): [T, (next: T) => void, () => void] {
  const storageKey = key ? `aioe.draft.${key}` : undefined;
  // `initial` is compared by value: its identity changes on every render.
  const initialJson = JSON.stringify(initial);
  const read = (): T => {
    if (storageKey) {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) return JSON.parse(raw) as T;
      } catch {
        /* storage unavailable or unreadable: start from the record */
      }
    }
    return JSON.parse(initialJson) as T;
  };
  const [value, setValue] = useState<T>(read);
  const [loadedFor, setLoadedFor] = useState(storageKey);
  // A different record: what was kept for it, or its own starting value.
  if (loadedFor !== storageKey) {
    setLoadedFor(storageKey);
    setValue(read());
  }
  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (!storageKey) return;
      try {
        if (JSON.stringify(next) === initialJson) sessionStorage.removeItem(storageKey);
        else sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* storage unavailable: the draft lasts as long as the page */
      }
    },
    [storageKey, initialJson],
  );
  const discard = useCallback(() => {
    if (storageKey) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* nothing kept */
      }
    }
    setValue(JSON.parse(initialJson) as T);
  }, [storageKey, initialJson]);
  return [value, set, discard];
}
